import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Modal, ModalDialog, Button, Stack } from '@mui/joy';
import FormFreeSoloMultiAutocomplete from 'components/technical/form/FormFreeSoloMultiAutocomplete';
import GFormProvider from 'components/technical/form/GFormProvider';
import SubmitButton from 'components/technical/form/SubmitButton';
import {
  useUpdatePortfolioTagsMutation,
  PortfolioDefDashboardInputDocument,
  GetPortfolioDefTagsDocument,
} from 'generated/graphql';
import { useFeedback } from 'components/technical/Feedback/UseFeedback';
import { GraphQLApiFormErrorMessage } from 'components/technical/form/GraphQLApiErrorMessage';
import { useGraphQLApiError } from 'components/technical/form/UseGraphQLApiError';
import GDialogHeader from 'components/technical/GDialog/GDialogHeader';
import { defaultRowSpacing } from 'components/StackSpacing';

interface FormData {
  tags: string[];
}

export interface EditPortfolioTagsDialogProps {
  onClose: () => void;
  portfolioDefinitionId: string;
  currentTags: string[];
  allTags: string[];
}

const validationSchema = yup.object({
  tags: yup.array().of(yup.string().required()).required(),
});

const normalizeTag = (tag: string): string => {
  return tag.toLowerCase().trim();
};

const normalizeTags = (tags: string[]): string[] => {
  return Array.from(new Set(tags.map(normalizeTag).filter((tag) => tag.length > 0))).sort();
};

const EditPortfolioTagsDialog = ({
  onClose,
  portfolioDefinitionId,
  currentTags,
  allTags,
}: EditPortfolioTagsDialogProps): ReactElement => {
  const { showSuccessMessage } = useFeedback();
  const [updateTags, { loading: isUpdating }] = useUpdatePortfolioTagsMutation();

  const methods = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      tags: currentTags,
    },
    mode: 'onChange',
  });

  const { onErrorAndThrow } = useGraphQLApiError(methods);

  const handleFormSubmit = async (formData: FormData): Promise<void> => {
    try {
      const normalizedTags = normalizeTags(formData.tags);

      await updateTags({
        variables: {
          input: {
            portfolioDefinitionId,
            tags: normalizedTags,
          },
        },
        refetchQueries: [PortfolioDefDashboardInputDocument, GetPortfolioDefTagsDocument],
      });

      showSuccessMessage('Portfolio tags updated successfully');
      onClose();
    } catch (error) {
      onErrorAndThrow(error);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog>
        <GFormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
            <GDialogHeader>Edit Portfolio Tags</GDialogHeader>
            <Stack spacing={defaultRowSpacing} alignItems="center">
              <Stack spacing={1.5} width="100%">
                <FormFreeSoloMultiAutocomplete
                  name="tags"
                  label="Tags"
                  options={allTags}
                  newEntryLabel="(New tag)"
                  limitTags={10}
                  width="fullWidth"
                  fullHeight={false}
                />
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button variant="outlined" color="neutral" onClick={onClose} disabled={isUpdating}>
                  Cancel
                </Button>
                <SubmitButton variant="solid" color="primary">
                  Save
                </SubmitButton>
              </Stack>
              <GraphQLApiFormErrorMessage />
            </Stack>
          </form>
        </GFormProvider>
      </ModalDialog>
    </Modal>
  );
};

export default EditPortfolioTagsDialog;
